/**
 * Form-input validators.
 *
 * Small predicate helpers that mirror server-side rules at the form layer.
 * Used to drive submit-button disabled state — the server is the final
 * authority on validation; these just keep the UI honest so the user
 * doesn't trigger a server round-trip with obviously empty inputs.
 */

/**
 * True iff `value` is a non-empty string that parses to a finite,
 * non-negative number. Used for required-price fields where the spec
 * mandates a non-negative currency value AND the value must be provided
 * (so empty does NOT silently mean zero).
 *
 * Spec references:
 *   §6.1 — "Client unit price — required, non-negative currency value"
 *           on PART
 *   §6.1 — "Supplier unit price — required, non-negative currency value"
 *           on SUB_PART
 *
 * Notes:
 *   - Pure whitespace counts as empty (a stray space shouldn't pass).
 *   - "0" passes — zero is non-negative. The server enforces business-
 *     level "is zero plausible?" separately if needed.
 *   - Scientific notation ("1e3") is rejected because <input type="number">
 *     in modern browsers won't emit it for currency inputs and accepting
 *     it surprises users. If a future requirement needs it, expand here.
 */
export function isValidNonNegativePriceInput(value: string): boolean {
  if (value.trim() === '') return false;
  // Reject anything that's not a plain decimal — guards against odd
  // pasted content like "1e3", "0x10", "1,000" (with comma) etc. The
  // form-field uses <input type="number"> which already constrains
  // input, but defence in depth helps when values are pasted.
  if (!/^-?\d+(\.\d+)?$/.test(value.trim())) return false;
  const n = Number(value);
  return Number.isFinite(n) && n >= 0;
}
